import { useTheme } from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import {
  KlydoReviewStatus,
  KlydoType,
  PopulatedKlydo,
  SoundMode,
} from '../../../api/klydo';
import {
  canReturnToEditing,
  getPublicationModalDefaults,
} from '../Publication/publicationState';
import { SOUND_MODE_DISPLAY_TEXT } from '../../../consts/general';
import useAppContext from '../../../hooks/useAppContext';

import KlydoAnimation from '../../../components/KlydoPlayer/KlydoAnimation';
import DSBox from '../../../design-system/DSBox';
import { COLLECTION_COVER_BORDER_RADIUS } from '../../../design-system/Collection';
import { DSTypography } from '../../../design-system/Typography';
import DSValueTypography from '../../../design-system/Typography/DSValueTypography';
import KlydoStatus from '../KlydosList/components/KlydoStatus';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import DSSecondaryButton from '../../../design-system/DSSecondaryButton';
import DSIconButton from '../../../design-system/DSIconButton';
import useKlydoReadOnlyView from '../hooks/useKlydoReadOnlyView';
import useSendKlydoToClock from '../hooks/useSendKlydoToClock';
import SendToClockButton from '../../../components/SendToClockButton';
import WarningDialog from '../../../components/WarningDialog';
import { KLYDO_YELLOW } from '../../../consts/colors';

import DSAvatar from '../../../design-system/DSAvatar';
import { CollectionType } from '../../../api/collections';
import { useState } from 'react';
import PublishModal, {
  PublicationTiming,
  PublishModalVariant,
} from '../Publication/PublishModal';
import {
  CANCEL_REVIEW_WARNING_MESSAGE,
  WITHDRAW_REQUEST_WARNING_MESSAGE,
} from '../../../consts/alerts';
import { ReactComponent as GlobalIcon } from '../../../design-system/Icons/Global.svg';

type KlydoReadOnlyViewProps = {
  klydo: PopulatedKlydo;
  onClickEdit: () => void;
};

const KlydoReadOnlyView = ({ klydo, onClickEdit }: KlydoReadOnlyViewProps) => {
  const { spacing, palette } = useTheme();
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const { isAdmin } = useAppContext();

  const publicReviewState = klydo?.public?.reviewStatus?.state;

  const isKlydoRejected = publicReviewState === KlydoReviewStatus.Rejected;

  const isKlydoPending = publicReviewState === KlydoReviewStatus.Pending;

  // Only a rejected DRAFT can go back to editing — see publicationState.ts for
  // why a sealed one must not be offered it.
  const canEdit = canReturnToEditing(klydo);

  const isKlydoSealed = klydo?.type === KlydoType.Sealed;

  const shouldShowCTAButton = isKlydoPending || canEdit;

  const shouldShowCollection =
    klydo?.collection?.type === CollectionType.Approved;

  // The design shows the reviewer's note only on a REJECTED klydo, in the error
  // colour — it is feedback to act on, not a record to keep displaying once the
  // klydo is approved.
  // A ban replaces the review status on this page, so its note replaces the
  // reviewer's (the review record underneath is untouched).
  const isKlydoBanned = !!klydo?.unlisted;
  const submissionNote = isKlydoBanned
    ? klydo?.unlistedMessage
    : isKlydoRejected
      ? klydo?.public?.reviewStatus?.message
      : undefined;
  const shouldShowSubmissionNotes = !!submissionNote;

  const {
    handleBackToEdit,
    handlePublish,
    handlePublishClick,
    publishModalOpen,
    publishModalVariant,
    handlePublishModalClose,
    calendarEvent,
    isChimePlaying,
    handleChimePlayPause,
    isStrikePlaying,
    handleStrikePlayPause,
  } = useKlydoReadOnlyView({
    klydo,
    onClickEdit,
  });

  const {
    prefilledTimestamp: prefilledPublicationTimestamp,
    isScheduledForLater,
  } = getPublicationModalDefaults(klydo, calendarEvent);

  // Only a finalized klydo can be sent to a clock.
  const sendToClock = useSendKlydoToClock(isKlydoSealed ? klydo : undefined);

  const SelectedPaletteCircle = ({ color }: { color: string }) => {
    return (
      <DSBox
        sx={{
          width: spacing(7),
          height: spacing(4),
          borderRadius: spacing(12.5),
          backgroundColor: color,
          border: `0.5px solid ${palette.text.secondary}`,
        }}
      />
    );
  };

  /**
   * The publication CTA, driven entirely by `publishModalVariant`.
   *
   * `Change` — reschedule the go-live date — is the ONLY action this page offers,
   * and only on an approved request that has not fired yet.
   *
   * There is deliberately no submit button here. A sealed klydo used to get one
   * (it cannot be edited, so a publication request was its only route to public),
   * but the design took it off this page: sending a klydo to review is now the
   * editor's "Done" CTA and nothing else. Do not add it back without the design
   * asking for it — and never as `!klydo.public.reviewStatus`, which offered it to
   * collection-sealed klydos and let them go public with no review at all.
   */
  const PublicationCTA = () => {
    if (publishModalVariant === PublishModalVariant.SetPublicationDate) {
      return (
        <DSPrimaryButton
          size="medium"
          onClick={handlePublishClick}
          startIcon={<GlobalIcon />}
        >
          Change
        </DSPrimaryButton>
      );
    }
    return null;
  };

  return (
    <>
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            width: '75%',
            flexDirection: 'column',
            gap: spacing(1),
          }}
        >
          <DSTypography variant="h3">{klydo?.name}</DSTypography>
          {klydo?.subtitle && (
            <DSTypography variant="body1" color="text.secondary">
              {klydo.subtitle}
            </DSTypography>
          )}
          <DSBox
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: spacing(2),
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <DSTypography variant="body1" color="text.primary">
              Last Update{' '}
              {new Date(klydo?.updatedAt || new Date()).toLocaleDateString(
                undefined,
                {
                  year: '2-digit',
                  month: '2-digit',
                  day: '2-digit',
                },
              )}
            </DSTypography>
            <KlydoStatus klydo={klydo} longPendingLabel />
          </DSBox>
        </DSBox>
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: spacing(1),
            flexShrink: 0,
          }}
        >
          <SendToClockButton {...sendToClock} />
          {shouldShowCTAButton && (
            <>
              {canEdit ? (
                <DSPrimaryButton
                  size="medium"
                  onClick={() => handleBackToEdit()}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Edit & resubmit
                </DSPrimaryButton>
              ) : (
                // Tertiary text link in the design, not a filled button: the
                // design system's text-variant secondary button (see theme's
                // `textSecondary`) plus an underline, the same treatment
                // PublishModal's "Previous" link uses.
                <DSSecondaryButton
                  size="medium"
                  onClick={() => setWarningDialogOpen(true)}
                  sx={{
                    whiteSpace: 'nowrap',
                    textDecoration: 'underline',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {/* A sealed klydo never becomes a draft again — withdrawing
                      only takes back the request, so the label must not
                      promise more. */}
                  {isKlydoSealed ? 'Withdraw request' : 'Switch to draft'}
                </DSSecondaryButton>
              )}
            </>
          )}
          {PublicationCTA()}
        </DSBox>
      </DSBox>
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          flex: 1,
          gap: spacing(3),
          minHeight: 0,
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing(2),
            flex: 1,
            overflowY: 'auto',
            paddingRight: spacing(2.5),
            marginRight: `-${spacing(2.5)}`,
            minHeight: 0,
          }}
        >
          {shouldShowCollection && (
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing(1),
              }}
            >
              <DSTypography variant="subtitle1">Collection</DSTypography>
              <DSBox
                sx={{
                  background: palette.secondary.main,
                  padding: '4%',
                  borderRadius: spacing(5),
                }}
              >
                <DSBox
                  sx={{
                    width: '100%',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    display: 'flex',
                  }}
                >
                  <DSBox
                    sx={{
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center',
                      display: 'flex',
                      gap: spacing(3),
                    }}
                  >
                    <DSAvatar
                      src={klydo?.collection?.coverUrl}
                      sx={{
                        width: spacing(10),
                        height: spacing(10),
                        borderRadius: COLLECTION_COVER_BORDER_RADIUS,
                      }}
                    />
                    <DSValueTypography>
                      {klydo?.collection?.name}
                    </DSValueTypography>
                  </DSBox>
                </DSBox>
              </DSBox>
            </DSBox>
          )}
          <DSBox
            sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}
          >
            <DSTypography variant="subtitle1"> Analytics </DSTypography>
            <DSBox
              sx={{
                background: palette.secondary.main,
                padding: '4%',
                borderRadius: spacing(5),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <DSTypography variant="body1">Favorites</DSTypography>
              <DSBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing(1),
                }}
              >
                <FavoriteBorderIcon sx={{ color: palette.text.primary }} />
                <DSValueTypography>
                  {klydo?.stats?.favorites || 0}
                </DSValueTypography>
              </DSBox>
            </DSBox>
          </DSBox>

          {shouldShowSubmissionNotes && (
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing(1),
              }}
            >
              <DSTypography variant="subtitle1">
                {isKlydoBanned ? 'Ban note' : 'Submission notes'}
              </DSTypography>
              <DSBox
                sx={{
                  background: palette.secondary.main,
                  padding: '4%',
                  borderRadius: spacing(5),
                }}
              >
                <DSValueTypography color="error">
                  {submissionNote}
                </DSValueTypography>
              </DSBox>
            </DSBox>
          )}
          <DSBox
            sx={{ display: 'flex', flexDirection: 'column', gap: spacing(1) }}
          >
            <DSTypography variant="subtitle1">Your Klydo</DSTypography>
            <DSBox
              sx={{
                background: palette.secondary.main,
                padding: '4%',
                borderRadius: spacing(5),
                display: 'flex',
                flexDirection: 'column',
                gap: spacing(3),
              }}
            >
              <DSBox
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <DSTypography variant="body1">Background Audio</DSTypography>
                <DSValueTypography>
                  {
                    SOUND_MODE_DISPLAY_TEXT[
                      klydo.capabilitiesV2?.animationSound?.mode ??
                        SoundMode.OFF
                    ]
                  }
                </DSValueTypography>
              </DSBox>
              {klydo.chimesUrls && klydo.chimesUrls?.length > 0 && (
                <DSBox
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    alignContent: 'center',
                  }}
                >
                  <DSTypography variant="body1">Chimes</DSTypography>
                  <DSBox
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: spacing(1),
                    }}
                  >
                    <DSValueTypography
                      sx={{
                        flexGrow: 1,
                        cursor: 'default',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: {
                          sm: '150px',
                          md: '200px',
                          lg: '400px',
                        },
                      }}
                    >
                      {klydo?.metadata?.chimesFileName}
                    </DSValueTypography>
                    <DSBox
                      sx={{
                        width: spacing(5),
                        height: spacing(5),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <DSIconButton
                        onClick={handleChimePlayPause}
                        sx={{
                          padding: spacing(0.5),
                          color: KLYDO_YELLOW,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 216, 72, 0.1)',
                          },
                        }}
                      >
                        {isChimePlaying ? <PauseIcon /> : <PlayArrowIcon />}
                      </DSIconButton>
                    </DSBox>
                  </DSBox>
                </DSBox>
              )}
              {isAdmin &&
                klydo.strikesUrls &&
                klydo.strikesUrls?.length > 0 && (
                  <DSBox
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <DSTypography variant="body1">Strike Tone</DSTypography>
                    <DSBox
                      sx={{
                        width: spacing(5),
                        height: spacing(5),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <DSIconButton
                        onClick={handleStrikePlayPause}
                        sx={{
                          padding: spacing(0.5),
                          color: KLYDO_YELLOW,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 216, 72, 0.1)',
                          },
                        }}
                      >
                        {isStrikePlaying ? <PauseIcon /> : <PlayArrowIcon />}
                      </DSIconButton>
                    </DSBox>
                  </DSBox>
                )}
              <DSBox
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <DSTypography variant="body1">Animation File</DSTypography>
                <DSBox
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: spacing(1),
                    alignItems: 'center',
                  }}
                >
                  <DSValueTypography
                    sx={{
                      flexGrow: 1,
                      cursor: 'default',
                    }}
                  >
                    {klydo?.metadata?.fileName}
                  </DSValueTypography>
                  <DSBox sx={{ width: spacing(5), height: spacing(5) }}>
                    <KlydoAnimation photoUrl={klydo.photoUrl} />
                  </DSBox>
                </DSBox>
              </DSBox>
              <DSBox
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <DSTypography variant="body1">Klydo Palette</DSTypography>
                <DSBox
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: spacing(1),
                  }}
                >
                  <SelectedPaletteCircle color={klydo.theme?.backgroundColor} />
                  <SelectedPaletteCircle color={klydo.theme?.dialsColor} />
                  <SelectedPaletteCircle color={klydo.theme?.handsColor} />
                  <SelectedPaletteCircle color={klydo.theme?.pendulumColor} />
                  <SelectedPaletteCircle
                    color={klydo.theme?.pendulumRodColor}
                  />
                </DSBox>
              </DSBox>
            </DSBox>
          </DSBox>
        </DSBox>
      </DSBox>
      {publishModalVariant && (
        <PublishModal
          open={publishModalOpen}
          onClose={handlePublishModalClose}
          onConfirm={handlePublish}
          defaultSelection={
            isScheduledForLater
              ? PublicationTiming.Scheduled
              : PublicationTiming.Automatic
          }
          prefilledTimestamp={prefilledPublicationTimestamp}
        />
      )}
      <WarningDialog
        warningMessage={
          isKlydoSealed
            ? WITHDRAW_REQUEST_WARNING_MESSAGE
            : CANCEL_REVIEW_WARNING_MESSAGE
        }
        open={warningDialogOpen}
        handleConfirmationClick={() => {
          setWarningDialogOpen(false);
          handleBackToEdit();
        }}
        handleClose={() => setWarningDialogOpen(false)}
      />
    </>
  );
};

export default KlydoReadOnlyView;
